import { useFormContext } from "react-hook-form";
import { motion } from "framer-motion";
import "./Switcher.scss";

const normalizeToBoolean = (val) => {
  if (typeof val === "boolean") return val;
  if (typeof val === "string") {
    const lowered = val.toLowerCase();
    return lowered === "true" || lowered === "1";
  }
  if (typeof val === "number") return val === 1;
  return false;
};

const Switcher = ({
  name,
  id,
  onChange,
  onLabel = "ON",
  offLabel = "OFF",
  validationRules = {},
  size = "default",
  // "mini" hides the on/off text baked into the default track (too small to fit it) — pass
  // this to show it as a separate label beside the track instead, e.g. when the switch is a
  // standalone filter field with nothing else around it to convey its state. Off by default
  // since existing "mini" usages often already have their own adjacent label (e.g. Cart.js's
  // %/Dhs toggle) that this would otherwise duplicate.
  showMiniLabel = false,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const rawValue = watch(name);
  const checked = normalizeToBoolean(rawValue);
  const error = errors?.[name];

  const handleChange = () => {
    const newValue = !checked;
    setValue(name, newValue, { shouldValidate: true });
    if (onChange) onChange(newValue);
  };

  // The "mini" track is too small to fit ON/OFF text inside it (unlike the default size,
  // which shows onLabel/offLabel inside the knob track itself) — its state is shown as a
  // separate label next to the track instead, e.g. for a filter form where the switch would
  // otherwise be the only field with no readable value.
  const isMini = size === "mini";

  return (
    <div className={`switcher-wrapper ${size} ${isMini && showMiniLabel ? "with-label" : ""}`}>
      {/* id defaults to name so Field's <label htmlFor={name}> toggles the switch when clicked */}
      <input
        type="checkbox"
        id={id ?? name}
        {...register(name, validationRules)}
        checked={checked}
        onChange={handleChange}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      />

      <div
        className="switcher"
        role="switch"
        aria-checked={checked}
        onClick={handleChange}
      >
        <motion.div
          className="switcher-knob"
          animate={{ x: checked ? (isMini ? 20 : 55) : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        />
        {!isMini && (
          <div className="switcher-labels">
            <span className={`label ${checked ? "active" : ""}`}>{onLabel}</span>
            <span className={`label ${!checked ? "active" : ""}`}>{offLabel}</span>
          </div>
        )}
      </div>

      {isMini && showMiniLabel && (
        <span
          className={`switcher-mini-label ${checked ? "active" : ""}`}
          onClick={handleChange}
        >
          {checked ? onLabel : offLabel}
        </span>
      )}

      {error && <div className="invalid-feedback">{error.message}</div>}
    </div>
  );
};

export default Switcher;
